import { Dispatch, SetStateAction, useMemo } from 'react'

import { AiTagOrderBy, AiTagSearchCondition } from '@/common/api/modules/aiTag'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import { useSiteId } from '@/common/app/site'
import { AITagMaster } from '@/common/model/recommend/aiTag'

import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import ListHeaderSortButton from '@/components/view/atoms/icon/ListHeaderSortButton'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import AITagListBulkOperationButton from '@/components/container/modules/recommend/aiTag/table/AITagListBulkOperationButton'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

import AITagListRow, { AITagEditForm } from './AITagListRow'

interface TheadProps extends ListFollowTableHeaderProps {
  selected: boolean
  allSelected: boolean
  onCheck(checked: boolean): void
  orderBy: AiTagOrderBy | null
  onChangeOrderBy(orderBy: AiTagOrderBy | null): void
  float?: boolean
}

const Thead = ({
  selected,
  allSelected,
  onCheck,
  orderBy,
  onChangeOrderBy,
  float = false,
  rowRefs,
  rowRects
}: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)

  return (
    <>
      <TableHeadItem checkbox sticky={0} float={float} {...rectProps(0)}>
        <CheckBox name={'all'} checked={selected} onChange={onCheck} incomplete={!allSelected} />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(1)}>
        <Text color={TextColor.SECONDARY} size={12}>
          タグ名
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(2)}>
        <Text color={TextColor.SECONDARY} size={12}>
          タグカテゴリ
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(3)}>
        <ListHeaderSortButton
          order={
            orderBy == AiTagOrderBy.IMPORTANCE_ASC
              ? 'asc'
              : orderBy == AiTagOrderBy.IMPORTANCE_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? AiTagOrderBy.IMPORTANCE_DESC : AiTagOrderBy.IMPORTANCE_ASC
            )
          }
          text='重要度'
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(4)}>
        <Text color={TextColor.SECONDARY} size={12}>
          作成日
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(5)}>
        <ListHeaderSortButton
          text='出現数'
          order={
            orderBy == AiTagOrderBy.OCCURRENCE_ASC
              ? 'asc'
              : orderBy == AiTagOrderBy.OCCURRENCE_DESC
                ? 'desc'
                : undefined
          }
          onChange={(order) =>
            onChangeOrderBy(
              order == 'desc' ? AiTagOrderBy.OCCURRENCE_DESC : AiTagOrderBy.OCCURRENCE_ASC
            )
          }
        />
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(6)}>
        <Text color={TextColor.SECONDARY} size={12}>
          リファレンス
        </Text>
      </TableHeadItem>
    </>
  )
}

interface Props {
  tags: AITagMaster[]
  total: number
  condition: AiTagSearchCondition
  setCondition: Dispatch<SetStateAction<AiTagSearchCondition>>
  onUpdatePage: (page: number) => void
  selectedIds: number[]
  onSelect: (id: number) => void
  onSelectAll: (checked: boolean) => void
  reload: () => void
  onPreview: (tag: AITagMaster) => void
}

const COLUMN_LENGTH = 8

const AITagListTable = ({
  tags,
  total,
  condition,
  setCondition,
  onUpdatePage,
  selectedIds,
  onSelect,
  onSelectAll,
  reload,
  onPreview
}: Props) => {
  const siteId = useSiteId()
  const allSelected = useMemo(
    () => tags.every((tag) => selectedIds.includes(tag.id)),
    [tags, selectedIds]
  )
  const listFollowProps = useListScrollFollow(COLUMN_LENGTH)
  const { header } = listFollowProps

  const theadProps: TheadProps = {
    selected: selectedIds.length > 0,
    allSelected,
    onCheck: onSelectAll,
    orderBy: condition.orderBy,
    onChangeOrderBy: (orderBy: AiTagOrderBy | null) => {
      setCondition((condition) => ({ ...condition, page: 1, orderBy }))
    },
    ...header
  }

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...theadProps} />}
      tableFloatHead={<Thead {...theadProps} float />}
      body={
        <>
          {tags.map((tag) => (
            <AITagListRow
              key={tag.id}
              siteId={siteId}
              tag={tag}
              isScrolled={header.isScrolled}
              selected={selectedIds.includes(tag.id)}
              onSelect={() => onSelect(tag.id)}
              onPreview={onPreview}
            />
          ))}
        </>
      }
      condition={condition}
      onUpdatePage={onUpdatePage}
      total={total}
      selectedIdsQuantity={selectedIds.length}
      selectedNode={
        <AITagListBulkOperationButton siteId={siteId} selectedIds={selectedIds} reload={reload} />
      }
    />
  )
}

export default AITagListTable
